// Host-facing HTTP implementation of VideoGenApi. Firebase-free: auth + base URL
// come from the injected BackendHttpDeps. Do NOT import from barrel exports
// (videoGen/index.ts).

import { VideoGenApi, VideoGenParams, VideoGenResult, VideoGenJobEvent, VideoGenJobStartedEvent } from './VideoGenApi'
import { resolveGameSessionToken } from '../http/backendHttpDeps'
import { RundotApiError, type ProviderErrorDetail } from '../errors'
import type { BackendHttpDeps } from '../http/backendHttpDeps'

export class HttpVideoGenApi implements VideoGenApi {
  private readonly appId: string
  private readonly deps: BackendHttpDeps

  constructor(appId: string, deps: BackendHttpDeps) {
    this.appId = appId
    this.deps = deps
  }

  async generate(params: VideoGenParams): Promise<VideoGenResult> {
    const idToken = await this.deps.getIdToken()
    const baseUrl = this.deps.getCloudRunUrl()
    const url = `${baseUrl}/v1/videogen/${this.appId}/sync-generate`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    }

    const gst = await resolveGameSessionToken(this.deps.getGameSessionToken);
    if (gst) headers['x-h5-game-session'] = gst;

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    })

    if (!response.ok) {
      const errorText = await response.text()
      let errorMessage = `Video generation failed (${response.status})`
      let errorCode = 'UNKNOWN'
      let detail: string | undefined
      let errorDetail: ProviderErrorDetail | undefined
      try {
        const errorJson = JSON.parse(errorText) as Record<string, unknown>
        errorMessage = (errorJson.error ?? errorJson.message ?? errorMessage) as string
        if (typeof errorJson.code === 'string') errorCode = errorJson.code
        if (typeof errorJson.detail === 'string') detail = errorJson.detail
        if (errorJson.errorDetail && typeof errorJson.errorDetail === 'object') {
          errorDetail = errorJson.errorDetail as ProviderErrorDetail
        }
      } catch {
        if (errorText) errorMessage = errorText
      }
      throw new RundotApiError(errorCode, errorMessage, response.status, detail, errorDetail)
    }

    return response.json()
  }

  async getCompletedJobs(): Promise<VideoGenJobEvent[]> {
    return []
  }

  async cancel(jobId: string): Promise<void> {
    const idToken = await this.deps.getIdToken()
    const baseUrl = this.deps.getCloudRunUrl()
    const url = `${baseUrl}/v1/jobs/${this.appId}/${jobId}/cancel`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    }

    const gst = await resolveGameSessionToken(this.deps.getGameSessionToken);
    if (gst) headers['x-h5-game-session'] = gst;

    const response = await fetch(url, {
      method: 'POST',
      headers,
    })

    if (!response.ok) {
      const errorText = await response.text()
      let errorMessage = `Job cancellation failed (${response.status})`
      try {
        const errorJson = JSON.parse(errorText)
        errorMessage = errorJson.error || errorJson.message || errorMessage
      } catch {
        if (errorText) errorMessage = errorText
      }
      throw new Error(errorMessage)
    }
  }

  onJobStarted(_callback: (event: VideoGenJobStartedEvent) => void): { unsubscribe: () => void } {
    return { unsubscribe: () => {} }
  }
}
